import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect } from 'react';

// @mui
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';

// components
import OTPPage from '../../../components/register_otp/otpPage';
import Iconify from '../../../components/iconify';

import FormProvider, { RHFTextField } from '../../../components/hook-form';
import { useDispatch, useSelector } from '../../../redux/store';
import { useAuthContext } from '../../../auth/useAuthContext';
import { verifyOtp, unsubscribeOTPAction } from '../../../redux/slices/user';

// ----------------------------------------------------------------------

export default function AuthRegisterForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, register } = useAuthContext();
  const { registerAction } = useSelector((state) => state.user);

  const [showPassword, setShowPassword] = useState(false);
  const [open, setOpen] = useState(false);

  const [registerPassword, setRegisterPassword] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');

  const RegisterSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('That is not an email'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password should be of minimum 6 characters length'),
    confirmPassword: Yup.string()
      .required('Confirm password is required')
      .oneOf([Yup.ref('password')], "Password's not match"),
  });

  const defaultValues = {
    email: '',
    password: '',
    confirmPassword: '',
  };

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = async (data) => {
    try {
      setRegisterPassword(data.password);
      setRegisterEmail(data.email);
      const resp = await register(data.email, data.password);

      if (resp === false) {
        reset();
        setError('afterSubmit', {
          message: 'The email address you specified is already in use',
        });
      } else if (resp === true) {
        setOpen(true);
      } else {
        reset();
        setError('afterSubmit', {
          message: resp,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (registerAction) {
      dispatch(unsubscribeOTPAction());
      if (typeof registerAction === 'string') {
        setError('afterSubmit', {
          message: registerAction,
        });
      } else {
        navigate('/');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerAction]);

  const CallOTP_Verification = async (otp) => {
    try {
      dispatch(verifyOtp(user?.UserID, methods.getValues().email, otp, '-', 3));
    } catch (error) {
      console.error(error);
    }
  };

  const handleResendOTP = () => {
    register(registerEmail, registerPassword);
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
      <Stack spacing={2.5}>
        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'carbon:view' : 'carbon:view-off'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <RHFTextField
          name="confirmPassword"
          label="Confirm Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'carbon:view' : 'carbon:view-off'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          Register
        </LoadingButton>

      </Stack>

      <OTPPage
        email={methods.getValues().email}
        CallOTP_Verification={CallOTP_Verification}
        onClose={() => setOpen(false)}
        open={open}
        ResendOTP={handleResendOTP}
        errorMsg={!!errors.afterSubmit && errors.afterSubmit.message }
        setError={setError}
      />
    </FormProvider>
  );
}
